import { Group, Paper, Stack, Switch, Text, Title } from '@mantine/core';
import type { JSX } from 'react';
import classes from './ConsentCard.module.css';

interface ConsentCardProps {
  title: string;
  description: string;
  active: boolean;
  loading: boolean;
  required: boolean;
  readOnly?: boolean;
  featured?: boolean;
  onToggle: (active: boolean) => void;
  /** Optional footer rendered full-width below the body (e.g. contact-channel form). */
  channelsForm?: JSX.Element;
  agreementAction?: JSX.Element;
}

export function ConsentCard({
  title,
  description,
  active,
  loading,
  readOnly = false,
  featured = false,
  onToggle,
  channelsForm,
  agreementAction,
}: ConsentCardProps): JSX.Element {
  const switchEl = (
    <Switch
      checked={active}
      onChange={(e) => onToggle(e.currentTarget.checked)}
      disabled={loading || readOnly}
      aria-label={`Toggle ${title}`}
      size="lg"
      color="polaris.5"
    />
  );
  const titleEl = (
    <Title order={3} fw={700} fz={18} lh="24px" c="black">
      {title}
    </Title>
  );
  return (
    <Paper
      withBorder
      radius="lg"
      className={classes.card}
      style={(theme) => ({
        borderWidth: 2,
        borderColor: active ? (theme.colors.polaris?.[5] ?? '#640040') : theme.colors.gray[3],
      })}
    >
      <Stack gap={12} className={classes.body} justify="flex-start">
        {featured ? (
          <Group justify="space-between" align="center" wrap="nowrap" gap="md">
            {titleEl}
            {switchEl}
          </Group>
        ) : (
          <Stack gap={12} align="flex-start">
            {switchEl}
            {titleEl}
          </Stack>
        )}
        <Text fz={13} c="dimmed" style={{ lineHeight: '18px' }}>
          {description}
        </Text>
        {agreementAction ? (
          <Group mt="auto" justify="flex-start">
            {agreementAction}
          </Group>
        ) : null}
      </Stack>
      {channelsForm}
    </Paper>
  );
}
